import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, PlusCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Zahlung {
  id: string;
  rechnung_id: string | null;
  betrag: number;
  datum: string;
  zahlungsart: string;
  absender: string | null;
  verwendungszweck: string | null;
  status: string;
  created_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

// ─── Platzhalter für Sub-Issue D (FinAPI Auto-Match) ─────────────────────────

function FinApiBannerSlot() {
  return (
    <div
      data-testid="finapi-banner-slot"
      className="rounded-md border border-dashed border-blue-200 bg-blue-50/50 p-3 text-xs text-blue-500 text-center"
    >
      FinAPI Auto-Match kommt in Kürze
    </div>
  );
}

// ─── Modal: Zahlungseingang erfassen ─────────────────────────────────────────

interface ZahlungErfassenModalProps {
  open: boolean;
  onClose: () => void;
  forderungsAkteId: string;
  rechnungsId: string | null;
  onSaved: (z: Zahlung) => void;
}

function ZahlungErfassenModal({
  open,
  onClose,
  forderungsAkteId,
  rechnungsId,
  onSaved,
}: ZahlungErfassenModalProps) {
  const { session } = useAuth();
  const [datum, setDatum] = useState('');
  const [betrag, setBetrag] = useState('');
  const [absender, setAbsender] = useState('');
  const [verwendungszweck, setVerwendungszweck] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    const betragNum = parseFloat(betrag.replace(',', '.'));
    if (isNaN(betragNum) || betragNum <= 0) {
      setError('Betrag muss eine positive Zahl sein');
      return;
    }
    if (!datum) {
      setError('Datum ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const resp = await fetch('/api/forderung-add-zahlungseingang', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
        body: JSON.stringify({
          forderungsakte_id: forderungsAkteId,
          rechnung_id: rechnungsId,
          betrag: betragNum,
          datum,
          absender: absender.trim(),
          verwendungszweck: verwendungszweck.trim(),
        }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error ?? 'Fehler beim Speichern');

      const newZahlung: Zahlung = {
        id: json.zahlung_id,
        rechnung_id: rechnungsId,
        betrag: betragNum,
        datum,
        zahlungsart: 'manuell',
        absender: absender.trim() || null,
        verwendungszweck: verwendungszweck.trim() || null,
        status: 'verbucht',
        created_at: new Date().toISOString(),
      };

      onSaved(newZahlung);
      setDatum('');
      setBetrag('');
      setAbsender('');
      setVerwendungszweck('');
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }, [forderungsAkteId, rechnungsId, datum, betrag, absender, verwendungszweck, onSaved, onClose]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Zahlungseingang erfassen</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <label className="text-xs font-medium">Datum *</label>
            <Input
              type="date"
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
              disabled={saving}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Betrag (€) *</label>
            <Input
              type="text"
              placeholder="z.B. 1500,00"
              value={betrag}
              onChange={(e) => setBetrag(e.target.value)}
              disabled={saving}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Absender</label>
            <Input
              type="text"
              placeholder="z.B. ADAC Versicherung AG"
              value={absender}
              onChange={(e) => setAbsender(e.target.value)}
              disabled={saving}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Verwendungszweck</label>
            <Input
              type="text"
              placeholder="z.B. Rechnung MG-2026-0099"
              value={verwendungszweck}
              onChange={(e) => setVerwendungszweck(e.target.value)}
              disabled={saving}
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={saving || !datum || !betrag}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface ZahlungseingaengeTabProps {
  forderungsAkteId: string;
  rechnungsId: string | null;
}

export function ZahlungseingaengeTab({ forderungsAkteId, rechnungsId }: ZahlungseingaengeTabProps) {
  const [zahlungen, setZahlungen] = useState<Zahlung[]>([]);
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    if (!rechnungsId) {
      setLoading(false);
      return;
    }

    supabase
      .from('zahlungseingaenge')
      .select('id,rechnung_id,betrag,datum,zahlungsart,absender,verwendungszweck,status,created_at')
      .eq('rechnung_id', rechnungsId)
      .order('datum', { ascending: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setLoadError(error.message);
        } else {
          setZahlungen((data ?? []) as Zahlung[]);
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [rechnungsId]);

  const handleSaved = useCallback((z: Zahlung) => {
    setZahlungen((prev) => [z, ...prev]);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  }, []);

  return (
    <div data-testid="zahlungseingaenge-tab-content" className="space-y-4 p-4">
      {/* FinAPI-Slot (Sub-Issue D) */}
      <FinApiBannerSlot />

      {/* Manuell erfassen */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Zahlungseingänge</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setModalOpen(true)}
          data-testid="zahlung-erfassen-btn"
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          Zahlungseingang erfassen
        </Button>
      </div>

      {saveSuccess && (
        <div className="flex items-center gap-1.5 text-xs text-green-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Zahlungseingang gespeichert.
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm">Lade Zahlungseingänge…</span>
        </div>
      ) : loadError ? (
        <p className="text-sm text-red-600">{loadError}</p>
      ) : !rechnungsId ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          Keine Rechnung verknüpft — Zahlungseingänge nicht abrufbar.
        </p>
      ) : zahlungen.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          Noch keine Zahlungseingänge erfasst.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="text-left py-1.5 pr-3 font-medium">Datum</th>
                <th className="text-left py-1.5 pr-3 font-medium">Absender</th>
                <th className="text-right py-1.5 pr-3 font-medium">Betrag</th>
                <th className="text-left py-1.5 pr-3 font-medium">Verwendungszweck</th>
                <th className="text-left py-1.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {zahlungen.map((z) => (
                <tr
                  key={z.id}
                  data-testid="zahlung-row"
                  className="border-b last:border-0 hover:bg-muted/40"
                >
                  <td className="py-2 pr-3">{formatDate(z.datum)}</td>
                  <td className="py-2 pr-3 truncate max-w-[120px]">{z.absender ?? '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums font-medium">
                    {formatCurrency(z.betrag)}
                  </td>
                  <td className="py-2 pr-3 truncate max-w-[160px]">
                    {z.verwendungszweck ?? '—'}
                  </td>
                  <td className="py-2">
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
                      {z.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ZahlungErfassenModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        forderungsAkteId={forderungsAkteId}
        rechnungsId={rechnungsId}
        onSaved={handleSaved}
      />
    </div>
  );
}
